/* eslint-disable max-len */
// src/components/PatientReview/PatientReview.component.tsx

import React from 'react'
import {
  Accordion,
  AccordionItem,
  DescriptionList,
} from '@enamelai/design-system'
import { Icon as FileIcon } from '@enamelai/design-system/icons/File'
import { Patient } from '@dataTypes/PatientInfo.types'
import type {
  Allergy,
  MedicalCondition,
} from '@dataTypes/allergy-medical-condition.types'
import { useAppSelector } from '@store/hooks'
import {
  formatTitleList,
  getPatientAllergyTitles,
  getPatientMedicalConditionTitles,
} from '@utils/patient-allergies-conditions.util'

type PatientReviewProps = {
  patientData: Patient
  kioskMode?: boolean
  allergiesCatalog?: Allergy[]
  medicalConditionsCatalog?: MedicalCondition[]
}

const EMPTY_VALUE = 'N/A'

const joinParts = (...parts: (string | null | undefined)[]): string =>
  parts
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ') || EMPTY_VALUE

const formatDateOfBirth = (dateString: string): string => {
  const date = new Date(dateString)
  if (!dateString || Number.isNaN(date.getTime())) return EMPTY_VALUE
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}

const formatFileSize = (size: number): string => {
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB']
  let unitIndex = 0
  let formattedSize = size

  while (formattedSize >= 1024 && unitIndex < units.length - 1) {
    formattedSize /= 1024
    unitIndex++
  }

  return `${formattedSize.toFixed(1)} ${units[unitIndex]}`
}

const formatPhoneNumber = (phone?: string | null): string => {
  if (!phone) return EMPTY_VALUE
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return phone
}

const PatientReview: React.FC<PatientReviewProps> = ({
  patientData,
  kioskMode = false,
  allergiesCatalog: suppliedAllergiesCatalog,
  medicalConditionsCatalog: suppliedMedicalConditionsCatalog,
}) => {
  const storedAllergiesCatalog = useAppSelector(
    (state) => state.allergyMedicalCondition.allergies,
  )
  const storedMedicalConditionsCatalog = useAppSelector(
    (state) => state.allergyMedicalCondition.medicalConditions,
  )
  const allergiesCatalog = suppliedAllergiesCatalog ?? storedAllergiesCatalog
  const medicalConditionsCatalog =
    suppliedMedicalConditionsCatalog ?? storedMedicalConditionsCatalog

  const sections = [
    {
      id: 'personal',
      title: 'Personal',
      showInfo: true,
      fields: [
        {
          label: 'Name',
          value: joinParts(
            patientData.first_name,
            patientData.middle_name,
            patientData.last_name,
          ),
        },
        {
          label: 'Date of birth',
          value: formatDateOfBirth(patientData.date_of_birth),
        },
        {
          label: 'Address',
          value: joinParts(
            patientData.address,
            patientData.city,
            patientData.state,
            patientData.zip,
          ),
        },
        { label: 'Gender', value: joinParts(patientData.gender) },
        {
          label: 'Marital status',
          value: joinParts(patientData.marital_status),
        },
        { label: 'Profession', value: joinParts(patientData.profession) },
        { label: 'Company', value: joinParts(patientData.company_name) },
        {
          label: 'Cell phone',
          value: formatPhoneNumber(patientData.cellphone),
        },
        { label: 'Email', value: joinParts(patientData.email) },
        {
          label: 'Home phone',
          value: formatPhoneNumber(patientData.home_phone),
        },
      ],
    },
    {
      id: 'background',
      title: 'Background',
      showInfo: true,
      fields: [
        {
          label: 'Allergies',
          value: formatTitleList(
            getPatientAllergyTitles(patientData, allergiesCatalog),
          ).replace('N/A', 'No'),
        },
        {
          label: 'Medical conditions',
          value: formatTitleList(
            getPatientMedicalConditionTitles(
              patientData,
              medicalConditionsCatalog,
            ),
          ).replace('N/A', 'No'),
        },
        ...(!kioskMode
          ? [
              {
                label: 'Any family members?',
                value:
                  patientData.relatives
                    ?.map((r) => r.family_member_name)
                    .join(', ') || 'No',
              },
            ]
          : []),
        {
          label: 'Preferred appointment times',
          value: patientData.preferred_appointment_time || 'No',
        },
        {
          label: 'Contact via',
          value:
            [
              patientData.contact_via_phone ? 'Phone' : '',
              patientData.contact_via_sms ? 'SMS' : '',
              patientData.contact_via_email ? 'Email' : '',
              patientData.contact_via_app ? 'App' : '',
            ]
              .filter(Boolean)
              .join(', ') || 'No',
        },
        {
          label: 'Preferred provider',
          value: patientData.preferred_provider_name || 'No',
        },
        {
          label: 'Preferred hygienist',
          value: patientData.preferred_hygienist_name || 'No',
        },
      ],
    },
    ...(!kioskMode
      ? [
          {
            id: 'forms',
            title: 'Forms',
            content:
              patientData.uploadedFiles &&
              patientData.uploadedFiles.length > 0 ? (
                <ul className="add-patient-files">
                  {patientData.uploadedFiles.map((file, index) => (
                    <li key={index} className="add-patient-files__item">
                      <FileIcon
                        className="add-patient-files__icon"
                        aria-hidden="true"
                      />
                      <div className="add-patient-files__meta">
                        <span className="add-patient-files__name">
                          {file.name}
                        </span>
                        <span className="add-patient-files__size">
                          {formatFileSize(file.size)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="add-patient-review__empty">No forms uploaded.</p>
              ),
          },
        ]
      : []),
  ]

  return (
    <div className="add-patient-step">
      <Accordion
        allowMultiple
        className="add-patient-review"
        defaultOpenIds={sections.map((section) => section.id)}
      >
        {sections.map((section) => (
          <AccordionItem
            id={section.id}
            key={section.id}
            title={section.title}
            showInfo={section.showInfo}
          >
            {section.fields ? (
              <DescriptionList
                columns={1}
                variant="plain"
                items={section.fields}
              />
            ) : (
              section.content
            )}
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  )
}

export default React.memo(PatientReview)
